import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../../styles/om-module.css';
import { useSiteDisplayName } from '../../../utils/siteUtils';
import {
    Calendar,
    Wrench,
    FileWarning,
    ChevronRight,
    ChevronLeft,
    ChevronDown,
    ChevronUp,
    Droplets,
    Scissors,
    CalendarDays,
    CalendarRange,
    ClipboardCheck,
} from 'lucide-react';

export type OMType = 'selector' | 'pm-calendar' | 'ptw' | 'module-cleaning' | 'grass-cutting' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export interface OMOption {
    id: Exclude<OMType, 'selector' | 'pm-calendar' | 'ptw'>;
    title: string;
    description: string;
    frequency: string;
    color: string;
}

export const omOptions: OMOption[] = [
    {
        id: 'module-cleaning',
        title: 'Module Cleaning',
        description: 'Solar panel cleaning and maintenance checklist for optimal performance',
        frequency: 'As Needed',
        color: 'blue'
    },
    {
        id: 'grass-cutting',
        title: 'Grass Cutting',
        description: 'Vegetation management and site maintenance checklist',
        frequency: 'As Needed',
        color: 'green'
    },
    {
        id: 'daily',
        title: 'Daily Checklist',
        description: 'Daily operations and maintenance inspection checklist',
        frequency: 'Daily',
        color: 'amber'
    },
    {
        id: 'weekly',
        title: 'Weekly Checklist',
        description: 'Weekly preventive maintenance and inspection checklist',
        frequency: 'Weekly',
        color: 'emerald'
    },
    {
        id: 'monthly',
        title: 'Monthly Checklist',
        description: 'Monthly comprehensive maintenance and inspection checklist',
        frequency: 'Monthly',
        color: 'violet'
    },
    {
        id: 'quarterly',
        title: 'Quarterly Checklist',
        description: 'Quarterly detailed inspection and maintenance checklist',
        frequency: 'Quarterly',
        color: 'orange'
    },
    {
        id: 'yearly',
        title: 'Yearly Checklist',
        description: 'Annual comprehensive inspection and maintenance checklist',
        frequency: 'Yearly',
        color: 'red'
    }
];

/**
 * @deprecated No longer used by OMSidebar — the navigation now uses a single
 * brand active state (see `.omx-nav__item` in styles/om-module.css) instead of
 * a per-item hue. Kept only so the named export stays stable; safe to delete
 * once no external consumer relies on it.
 */
export const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; border: string; text: string; hover: string; activeBg: string }> = {
        blue: {
            bg: 'bg-[#F2F8FC]',
            border: 'border-[#C2DFF1] hover:border-[#3EA3DA]',
            text: 'text-[#0066A3]',
            hover: 'hover:bg-[#E3F0F9]',
            activeBg: 'bg-[#E3F0F9]'
        },
        green: {
            bg: 'bg-green-50',
            border: 'border-green-200 hover:border-green-400',
            text: 'text-green-600',
            hover: 'hover:bg-green-100',
            activeBg: 'bg-green-100'
        },
        amber: {
            bg: 'bg-amber-50',
            border: 'border-amber-200 hover:border-amber-400',
            text: 'text-amber-600',
            hover: 'hover:bg-amber-100',
            activeBg: 'bg-amber-100'
        },
        emerald: {
            bg: 'bg-emerald-50',
            border: 'border-emerald-200 hover:border-emerald-400',
            text: 'text-emerald-600',
            hover: 'hover:bg-emerald-100',
            activeBg: 'bg-emerald-100'
        },
        violet: {
            bg: 'bg-[#F2F8FC]',
            border: 'border-[#C2DFF1] hover:border-[#3EA3DA]',
            text: 'text-[#0066A3]',
            hover: 'hover:bg-[#E3F0F9]',
            activeBg: 'bg-[#E3F0F9]'
        },
        orange: {
            bg: 'bg-orange-50',
            border: 'border-orange-200 hover:border-orange-400',
            text: 'text-orange-600',
            hover: 'hover:bg-orange-100',
            activeBg: 'bg-orange-100'
        },
        red: {
            bg: 'bg-red-50',
            border: 'border-red-200 hover:border-red-400',
            text: 'text-red-600',
            hover: 'hover:bg-red-100',
            activeBg: 'bg-red-100'
        }
    };
    return colors[color] || colors.blue;
};

export const getIconByType = (type: string, size: number = 24) => {
    switch (type) {
        case 'module-cleaning': return <Droplets size={size} />;
        case 'grass-cutting': return <Scissors size={size} />;
        case 'daily': return <Calendar size={size} />;
        case 'weekly': return <CalendarDays size={size} />;
        case 'monthly': return <CalendarRange size={size} />;
        case 'quarterly': return <ClipboardCheck size={size} />;
        case 'yearly': return <ClipboardCheck size={size} />;
        default: return <ClipboardCheck size={size} />;
    }
};

interface OMSidebarProps {
    site: string;
    siteName?: string;
    activeOM: string;
    onChecklistClick: (omId: string) => void;
    onCompactChange?: (isCompact: boolean) => void;
    onWidthChange?: (width: number) => void;
}

const OMSidebar: React.FC<OMSidebarProps> = ({ site, siteName, activeOM, onChecklistClick, onCompactChange, onWidthChange }) => {
    const navigate = useNavigate();
    const displaySiteName = useSiteDisplayName(site, siteName);
    const MIN_WIDTH = 80; // Minimum width (compact - 5rem)
    const MAX_WIDTH = 288; // Maximum width (w-72)
    const COMPACT_THRESHOLD = 100; // Width at which to show only icons

    const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
        try {
            const saved = localStorage.getItem('om.sidebar.width');
            return saved ? Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, parseInt(saved, 10))) : MAX_WIDTH;
        } catch {
            return MAX_WIDTH;
        }
    });

    const [isResizing, setIsResizing] = useState(false);
    const [quickLinksOpen, setQuickLinksOpen] = useState(true);
    const [checklistsOpen, setChecklistsOpen] = useState(true);

    const isCompact = sidebarWidth < COMPACT_THRESHOLD;

    // Smooth toggle animation
    const toggleCollapse = () => {
        if (isCompact) {
            // Expand
            setSidebarWidth(MAX_WIDTH);
        } else {
            // Collapse
            setSidebarWidth(MIN_WIDTH);
        }
    };

    useEffect(() => {
        try {
            localStorage.setItem('om.sidebar.width', sidebarWidth.toString());
        } catch {
            // No-op if storage is blocked.
        }
        if (isCompact) {
            setQuickLinksOpen(true);
            setChecklistsOpen(true);
        }
        onCompactChange?.(isCompact);
        onWidthChange?.(sidebarWidth);
    }, [sidebarWidth, isCompact, onCompactChange, onWidthChange]);

    // Handle mouse drag to resize
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return;

            const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, e.clientX - 56)); // 3.5rem = 56px for left nav
            setSidebarWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'col-resize';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'auto';
            document.body.style.cursor = 'auto';
        };
    }, [isResizing]);

    return (
        <div
            className={`om-sidebar omx fixed left-[3.5rem] top-16 h-[calc(100vh-64px)] border-r z-40 transition-[width] duration-300`}
            style={{ width: `${sidebarWidth}px`, borderColor: '#DFE6EE', background: '#FFFFFF' }}
        >
            <nav className={`omx-nav${isCompact ? ' is-compact' : ''}`} aria-label="O&M navigation">

                {/* Module identity */}
                <div className="omx-nav__head">
                    {isCompact ? (
                        <Wrench size={17} strokeWidth={1.8} style={{ color: '#0084CF' }} />
                    ) : (
                        <div style={{ minWidth: 0 }}>
                            <div className="omx-nav__module">Operations &amp; Maintenance</div>
                            <div className="omx-nav__site" title={displaySiteName}>{displaySiteName}</div>
                        </div>
                    )}
                </div>

                <div className="omx-nav__scroll">

                    {/* ── Quick Links ─────────────────────────────────── */}
                    {!isCompact && (
                        <button
                            type="button"
                            className="omx-nav__group"
                            onClick={() => setQuickLinksOpen(prev => !prev)}
                            aria-expanded={quickLinksOpen}
                        >
                            <span>Quick Links</span>
                            {quickLinksOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        </button>
                    )}

                    {quickLinksOpen && (
                        <>
                            <button
                                type="button"
                                onClick={() => navigate(`/om/${site}`)}
                                title="PM Calendar"
                                aria-current={activeOM === 'selector' ? 'page' : undefined}
                                className={`omx-nav__item${activeOM === 'selector' ? ' is-active' : ''}`}
                            >
                                <span className="omx-nav__icon"><Calendar size={16} strokeWidth={1.8} /></span>
                                {!isCompact && (
                                    <span style={{ minWidth: 0 }}>
                                        <span className="omx-nav__label">PM Calendar</span>
                                        <span className="omx-nav__meta">Preventive Maintenance Schedule</span>
                                    </span>
                                )}
                                {!isCompact && <ChevronRight size={14} className="omx-nav__chev" />}
                            </button>

                            <button
                                type="button"
                                onClick={() => navigate(`/ptw/${site}`)}
                                title="PTW"
                                aria-current={activeOM === 'ptw' ? 'page' : undefined}
                                className={`omx-nav__item${activeOM === 'ptw' ? ' is-active' : ''}`}
                            >
                                <span className="omx-nav__icon"><FileWarning size={16} strokeWidth={1.8} /></span>
                                {!isCompact && (
                                    <span style={{ minWidth: 0 }}>
                                        <span className="omx-nav__label">PTW</span>
                                        <span className="omx-nav__meta">Permit to Work</span>
                                    </span>
                                )}
                                {!isCompact && <ChevronRight size={14} className="omx-nav__chev" />}
                            </button>
                        </>
                    )}

                    {/* ── Checklists ──────────────────────────────────── */}
                    {!isCompact && (
                        <button
                            type="button"
                            className="omx-nav__group"
                            onClick={() => setChecklistsOpen(prev => !prev)}
                            aria-expanded={checklistsOpen}
                            style={{ marginTop: 6 }}
                        >
                            <span>Checklists</span>
                            {checklistsOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        </button>
                    )}

                    {checklistsOpen && omOptions.map((option) => {
                        const isActive = activeOM === option.id;
                        return (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => onChecklistClick(option.id)}
                                title={option.title}
                                aria-current={isActive ? 'page' : undefined}
                                className={`omx-nav__item${isActive ? ' is-active' : ''}`}
                            >
                                <span className="omx-nav__icon">{getIconByType(option.id, 16)}</span>
                                {!isCompact && (
                                    <span style={{ minWidth: 0 }}>
                                        <span className="omx-nav__label">{option.title}</span>
                                        <span className="omx-nav__meta">{option.frequency}</span>
                                    </span>
                                )}
                                {!isCompact && <ChevronRight size={14} className="omx-nav__chev" />}
                            </button>
                        );
                    })}
                </div>
            </nav>

            {/* Resize handle + collapse toggle */}
            <div
                onMouseDown={() => setIsResizing(true)}
                className={`omx-nav__resize${isResizing ? ' is-active' : ''}`}
                title="Drag to resize"
            />
            <button
                type="button"
                onClick={toggleCollapse}
                className="omx-nav__toggle"
                title={isCompact ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-label={isCompact ? 'Expand sidebar' : 'Collapse sidebar'}
            >
                {isCompact ? <ChevronRight size={13} strokeWidth={2.2} /> : <ChevronLeft size={13} strokeWidth={2.2} />}
            </button>
        </div>
    );
};

export default OMSidebar;
